import * as React from "react";
import { useCampaignFilterState } from "@/hooks/use-campaign-filter-state";
import { cn } from "@/lib/utils";

export interface CampaignLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  originalStorage: string;
  nextStorage: string;
}

export const CampaignLink = React.forwardRef<
  HTMLAnchorElement,
  CampaignLinkProps
>((props, ref) => {
  const { className, href, originalStorage, nextStorage, ...rest } = props;

  const { filterState } = useCampaignFilterState(originalStorage);
  const { setFilterState } = useCampaignFilterState(nextStorage);

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      // Check if any modifier key is pressed (cmd/ctrl for new tab, shift for new window)
      const isModifierKeyPressed = e.metaKey || e.ctrlKey || e.shiftKey;

      // If modifier key is pressed, let the browser handle the click naturally
      if (isModifierKeyPressed) {
        // Still need to set the filter state for the new tab
        setFilterState(filterState);
        // Don't prevent default - let browser open in new tab/window
        return;
      }

      // For regular clicks, prevent default and handle navigation manually
      e.preventDefault();
      e.stopPropagation();
      setFilterState(filterState);
      if (typeof window === "object") {
        if (window?.location && href) {
          window.location.href = href;
        }
      }
    },
    [href, filterState, setFilterState],
  );

  return (
    <a
      {...rest}
      ref={ref}
      href={href}
      onClick={handleClick}
      className={cn(className, "cursor-pointer")}
    />
  );
});

CampaignLink.displayName = "CampaignLink";
